import { useRef, useState, useEffect, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/lib/supabase';
import TurnstileWidget, { type TurnstileWidgetHandle } from '@/components/feature/TurnstileWidget';
import { verifyTurnstileToken } from '@/lib/turnstile';

interface TourSummary {
  id: string;
  title: string;
  price: number;
  duration: string;
}

export interface BookingResult {
  created: boolean;
  email: string;
}

interface BookingFormProps {
  tour: TourSummary;
  userEmail: string;
  userFullName: string;
  initialAdults?: number;
  initialChildren?: number;
  accommodation?: string;
  onSuccess: (result: BookingResult) => void;
  onDiscountChange?: (info: {
    applied: AppliedDiscount | null;
    total: number;
    finalTotal: number;
  }) => void;
}

interface AppliedDiscount {
  code: string;
  type: 'percent' | 'fixed';
  value: number;
  discount: number;
  discountedTotal: number;
}

function extractDestination(title: string): string {
  return title
    .split(' - ')[0]
    .replace(/\s*\(.*\)\s*$/, '')
    .replace(/\s*(Tour|Day Trip|Trekking|Cruise|Package)\s*$/i, '')
    .trim();
}

export default function BookingForm({ tour, userEmail, userFullName, initialAdults, initialChildren, accommodation, onSuccess, onDiscountChange }: BookingFormProps) {
  const { t } = useTranslation();
  const [fullName, setFullName] = useState(userFullName);
  const [email, setEmail] = useState(userEmail);
  const [phone, setPhone] = useState('');
  const [adults, setAdults] = useState(initialAdults ?? 1);
  const [children, setChildren] = useState(initialChildren ?? 0);
  const [travelDate, setTravelDate] = useState('');
  const [specialRequests, setSpecialRequests] = useState(
    accommodation ? `Preferred accommodation: ${accommodation}` : ''
  );
  const [paymentMethod, setPaymentMethod] = useState('bank_transfer');
  const [pickupPoint, setPickupPoint] = useState('');
  const [dropoffPoint, setDropoffPoint] = useState('');
  const [fullAddress, setFullAddress] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const turnstileRef = useRef<TurnstileWidgetHandle>(null);
  const [discountInput, setDiscountInput] = useState('');
  const [applied, setApplied] = useState<AppliedDiscount | null>(null);
  const [discountError, setDiscountError] = useState<string | null>(null);
  const [applying, setApplying] = useState(false);

  const price = Number(tour.price) || 0;
  const total = Math.round(price * adults + (price / 2) * children);
  const finalTotal = applied ? applied.discountedTotal : total;
  const today = new Date().toISOString().split('T')[0];

  useEffect(() => {
    setApplied(null);
    setDiscountError(null);
  }, [adults, children]);

  useEffect(() => {
    if (onDiscountChange) {
      onDiscountChange({ applied, total, finalTotal });
    }
  }, [applied, total, finalTotal, onDiscountChange]);

  const inputClass =
    'w-full border border-secondary-200 rounded-md px-3.5 py-2.5 text-sm text-secondary-800 placeholder:text-secondary-400 bg-white focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition';

  const reasonMessage: Record<string, string> = {
    invalid: t('booking.discountInvalid'),
    inactive: t('booking.discountInactive'),
    expired: t('booking.discountExpired'),
    limit_reached: t('booking.discountLimit'),
    tour_mismatch: t('booking.discountMismatch'),
  };

  const applyDiscount = async () => {
    setDiscountError(null);
    const code = discountInput.trim();
    if (!code) {
      setApplied(null);
      return;
    }
    setApplying(true);
    try {
      const { data, error } = await supabase.functions.invoke('vietnamtravel-validate-discount', {
        body: { code, subtotal: total, tour_id: tour.id },
      });
      if (error) throw error;
      if (!data || data.ok !== true) {
        throw new Error((data && data.error) || t('booking.discountFailed'));
      }
      if (!data.applied) {
        setApplied(null);
        setDiscountError(reasonMessage[data.reason] || t('booking.discountNotValid'));
        return;
      }
      setApplied({
        code: data.code,
        type: data.type,
        value: data.value,
        discount: data.discount,
        discountedTotal: data.discountedTotal,
      });
    } catch (err) {
      setDiscountError(err instanceof Error ? err.message : t('booking.discountFailed'));
    } finally {
      setApplying(false);
    }
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormError(null);

    if (!fullName.trim() || !email.trim() || !phone.trim() || !travelDate) {
      setFormError(t('booking.fillRequired'));
      return;
    }

    if (!turnstileToken) {
      setFormError(t('booking.captchaRequired'));
      return;
    }

    const captchaOk = await verifyTurnstileToken(turnstileToken);
    if (!captchaOk) {
      setFormError(t('booking.captchaFailed'));
      turnstileRef.current?.reset();
      setTurnstileToken(null);
      return;
    }

    setSubmitting(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();

      const booking = {
        tour_id: tour.id,
        tour_title: tour.title,
        destination: extractDestination(tour.title),
        full_name: fullName.trim(),
        email: email.trim(),
        phone: phone.trim(),
        adults,
        children,
        travel_date: travelDate,
        special_requests: specialRequests.trim(),
        total_price: total,
        status: 'Pending',
      };

      const { data, error } = await supabase.functions.invoke('vietnamtravel-create-booking', {
        body: {
          user_id: user?.id ?? null,
          email: email.trim(),
          full_name: fullName.trim(),
          phone: phone.trim(),
          subtotal: total,
          tour_price: price,
          discount_code: applied?.code ?? '',
          page_url: window.location.href,
          booking: {
            ...booking,
            total_price: finalTotal,
          },
          ufd: {
            payment_method: paymentMethod,
            pickup_point: pickupPoint.trim(),
            dropoff_point: dropoffPoint.trim(),
            full_address: fullAddress.trim(),
            tour_duration: tour.duration,
          },
        },
      });

      if (error) throw error;

      if (!data || data.ok !== true) {
        throw new Error((data && data.error) || t('booking.submitFailed'));
      }

      turnstileRef.current?.reset();
      setTurnstileToken(null);
      onSuccess({
        created: !!data.created,
        email: data.email || email.trim(),
      });
    } catch (err) {
      setFormError(err instanceof Error ? err.message : t('booking.submitFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6" noValidate>
      {/* Contact details */}
      <section className="bg-white rounded-lg border border-secondary-200 p-5 md:p-6">
        <h2 className="font-heading text-base font-bold text-secondary-900 mb-4 flex items-center gap-2">
          <span className="w-8 h-8 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center">
            <i className="ri-user-line text-base" />
          </span>
          {t('booking.contactDetails')}
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium text-secondary-700 mb-1.5 block">
              {t('booking.fullName')} <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="full_name"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              placeholder={t('booking.namePlaceholder')}
              className={inputClass}
            />
          </div>
          <div>
            <label className="text-sm font-medium text-secondary-700 mb-1.5 block">
              {t('booking.email')} <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              name="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t('booking.emailPlaceholder')}
              className={inputClass}
            />
          </div>
          <div className="sm:col-span-2">
            <label className="text-sm font-medium text-secondary-700 mb-1.5 block">
              {t('booking.phone')} <span className="text-red-500">*</span>
            </label>
            <input
              type="tel"
              name="phone"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="+84 ..."
              className={inputClass}
            />
          </div>
          <div className="sm:col-span-2">
            <label className="text-sm font-medium text-secondary-700 mb-1.5 block">
              {t('booking.fullAddress')}
            </label>
            <input
              type="text"
              name="address.full_address"
              value={fullAddress}
              onChange={(e) => setFullAddress(e.target.value)}
              placeholder={t('booking.addressPlaceholder')}
              className={inputClass}
            />
          </div>
        </div>
      </section>

      {/* Trip details */}
      <section className="bg-white rounded-lg border border-secondary-200 p-5 md:p-6">
        <h2 className="font-heading text-base font-bold text-secondary-900 mb-4 flex items-center gap-2">
          <span className="w-8 h-8 rounded-full bg-accent-100 text-accent-900 flex items-center justify-center">
            <i className="ri-calendar-line text-base" />
          </span>
          {t('booking.tripDetails')}
        </h2>

        <div className="mb-5">
          <label className="text-sm font-medium text-secondary-700 mb-1.5 block">
            {t('booking.travelDate')} <span className="text-red-500">*</span>
          </label>
          <input
            type="date"
            name="travel_date"
            value={travelDate}
            min={today}
            onChange={(e) => setTravelDate(e.target.value)}
            className={inputClass}
          />
          <p className="mt-1.5 text-[11px] text-secondary-400">
            {t('booking.datesFlexible')}
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium text-secondary-700 mb-1.5 block">{t('booking.adults')}</label>
            <div className="flex items-center border border-secondary-200 rounded-md">
              <button
                type="button"
                onClick={() => setAdults(Math.max(1, adults - 1))}
                className="w-10 h-10 flex items-center justify-center text-secondary-500 hover:text-secondary-700 cursor-pointer"
              >
                <i className="ri-subtract-line" />
              </button>
              <span className="flex-1 text-center text-sm font-semibold text-secondary-800">
                {String(adults).padStart(2, '0')}
              </span>
              <button
                type="button"
                onClick={() => setAdults(adults + 1)}
                className="w-10 h-10 flex items-center justify-center text-secondary-500 hover:text-secondary-700 cursor-pointer"
              >
                <i className="ri-add-line" />
              </button>
            </div>
          </div>
          <div>
            <label className="text-sm font-medium text-secondary-700 mb-1.5 block">{t('booking.children')}</label>
            <div className="flex items-center border border-secondary-200 rounded-md">
              <button
                type="button"
                onClick={() => setChildren(Math.max(0, children - 1))}
                className="w-10 h-10 flex items-center justify-center text-secondary-500 hover:text-secondary-700 cursor-pointer"
              >
                <i className="ri-subtract-line" />
              </button>
              <span className="flex-1 text-center text-sm font-semibold text-secondary-800">
                {String(children).padStart(2, '0')}
              </span>
              <button
                type="button"
                onClick={() => setChildren(children + 1)}
                className="w-10 h-10 flex items-center justify-center text-secondary-500 hover:text-secondary-700 cursor-pointer"
              >
                <i className="ri-add-line" />
              </button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
          <div>
            <label className="text-sm font-medium text-secondary-700 mb-1.5 block">
              {t('booking.pickupPoint')}
            </label>
            <input
              type="text"
              name="travel.pickup_point"
              value={pickupPoint}
              onChange={(e) => setPickupPoint(e.target.value)}
              placeholder={t('booking.pickupPlaceholder')}
              className={inputClass}
            />
          </div>
          <div>
            <label className="text-sm font-medium text-secondary-700 mb-1.5 block">
              {t('booking.dropoffPoint')}
            </label>
            <input
              type="text"
              name="travel.dropoff_point"
              value={dropoffPoint}
              onChange={(e) => setDropoffPoint(e.target.value)}
              placeholder={t('booking.dropoffPlaceholder')}
              className={inputClass}
            />
          </div>
        </div>
      </section>

      {/* Special requests */}
      <section className="bg-white rounded-lg border border-secondary-200 p-5 md:p-6">
        <h2 className="font-heading text-base font-bold text-secondary-900 mb-4 flex items-center gap-2">
          <span className="w-8 h-8 rounded-full bg-secondary-100 text-secondary-600 flex items-center justify-center">
            <i className="ri-chat-3-line text-base" />
          </span>
          {t('booking.specialRequests')}
        </h2>
        <textarea
          name="special_requests"
          value={specialRequests}
          maxLength={500}
          onChange={(e) => setSpecialRequests(e.target.value)}
          rows={4}
          placeholder={t('booking.specialPlaceholder')}
          className={`${inputClass} resize-none`}
        />
        <p className="mt-1.5 text-right text-[11px] text-secondary-400">
          {t('booking.charCount', { n: specialRequests.length })}
        </p>
      </section>

      {/* Payment */}
      <section className="bg-white rounded-lg border border-secondary-200 p-5 md:p-6">
        <h2 className="font-heading text-base font-bold text-secondary-900 mb-4 flex items-center gap-2">
          <span className="w-8 h-8 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center">
            <i className="ri-bank-card-line text-base" />
          </span>
          {t('booking.payment')}
        </h2>
        <div>
          <label className="text-sm font-medium text-secondary-700 mb-1.5 block">
            {t('booking.paymentMethod')}
          </label>
          <select
            name="payment.method"
            value={paymentMethod}
            onChange={(e) => setPaymentMethod(e.target.value)}
            className={inputClass}
          >
            <option value="bank_transfer">{t('booking.bankTransfer')}</option>
            <option value="credit_card">{t('booking.creditCard')}</option>
            <option value="momo">{t('booking.momo')}</option>
            <option value="zalopay">{t('booking.zalopay')}</option>
            <option value="cod">{t('booking.cod')}</option>
          </select>
        </div>
      </section>

      {/* Discount code */}
      <section className="bg-white rounded-lg border border-secondary-200 p-5 md:p-6">
        <h2 className="font-heading text-base font-bold text-secondary-900 mb-4 flex items-center gap-2">
          <span className="w-8 h-8 rounded-full bg-accent-100 text-accent-900 flex items-center justify-center">
            <i className="ri-coupon-3-line text-base" />
          </span>
          {t('booking.discountCode')}
        </h2>

        <div className="flex flex-col sm:flex-row gap-3">
          <input
            type="text"
            name="discount_code"
            value={discountInput}
            onChange={(e) => setDiscountInput(e.target.value)}
            placeholder={t('booking.discountPlaceholder')}
            className={`${inputClass} uppercase`}
          />
          <button
            type="button"
            onClick={applyDiscount}
            disabled={applying}
            className="inline-flex items-center justify-center gap-2 bg-secondary-500 hover:bg-secondary-600 disabled:opacity-60 text-white font-semibold text-sm px-5 py-2.5 rounded-md transition-colors whitespace-nowrap cursor-pointer"
          >
            {applying ? <i className="ri-loader-4-line animate-spin text-base" /> : t('booking.apply')}
          </button>
        </div>

        {discountError && (
          <p className="mt-2 text-xs text-red-500 flex items-center gap-1.5">
            <i className="ri-error-warning-line" />
            {discountError}
          </p>
        )}

        {applied && (
          <div className="mt-3 flex items-center justify-between gap-3 bg-green-50 border border-green-100 rounded-md px-4 py-3">
            <div className="flex items-center gap-2">
              <i className="ri-checkbox-circle-line text-green-600 text-base" />
              <div>
                <p className="text-sm font-semibold text-green-700">{t('booking.applied', { code: applied.code })}</p>
                <p className="text-[11px] text-green-600">
                  {applied.type === 'percent'
                    ? t('booking.percentOff', { value: applied.value })
                    : t('booking.fixedOff', { value: applied.value.toLocaleString() })}
                </p>
              </div>
            </div>
            <p className="text-sm font-bold text-green-700 whitespace-nowrap">
              −${applied.discount.toLocaleString()}
            </p>
          </div>
        )}
      </section>

      {/* Error */}
      {formError && (
        <div className="flex items-start gap-2 bg-red-50 border border-red-100 rounded-lg px-4 py-3">
          <i className="ri-error-warning-line text-red-500 text-sm mt-0.5 shrink-0" />
          <p className="text-sm text-red-600">{formError}</p>
        </div>
      )}

      <TurnstileWidget ref={turnstileRef} onVerify={setTurnstileToken} />

      {/* Submit */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-4 bg-white rounded-lg border border-secondary-200 p-5">
        <div>
          <p className="text-xs text-secondary-500">{t('booking.estimatedTotal')}</p>
          <div className="flex items-baseline gap-2">
            <p className="font-heading text-2xl font-bold text-secondary-900">
              ${finalTotal.toLocaleString()}
            </p>
            {applied && (
              <p className="text-sm text-secondary-400 line-through">
                ${total.toLocaleString()}
              </p>
            )}
          </div>
          <p className="mt-0.5 text-[11px] text-secondary-400">
            {adults} {t('booking.adultsUnit')} × ${price.toLocaleString()}
            {children > 0 ? ` + ${children} ${t('booking.childrenUnit')} × ${Math.round(price / 2).toLocaleString()} (${t('booking.halfPrice')})` : ''} USD
          </p>
        </div>
        <button
          type="submit"
          disabled={submitting}
          className="inline-flex items-center justify-center gap-2 bg-primary-500 hover:bg-primary-600 disabled:opacity-60 text-white font-semibold text-sm px-6 py-3 rounded-full transition-colors whitespace-nowrap cursor-pointer"
        >
          {submitting ? (
            <>
              <i className="ri-loader-4-line animate-spin text-base" />
              {t('booking.submitting')}
            </>
          ) : (
            <>
              {t('booking.confirmBooking')}
              <i className="ri-arrow-right-line text-base" />
            </>
          )}
        </button>
      </div>

      <p className="text-center text-[11px] text-secondary-400 flex items-center justify-center gap-1.5">
        <i className="ri-shield-check-line text-primary-500" />
        {t('booking.nothingCharged')}
      </p>
    </form>
  );
}